/*
 * Home on a phone, like the old Steward's (03-home.js renderHome, owner 2026-10-29: "home more like the previous steward"):
 * the greeting, a big map of today's stops, today's visits as cards to swipe (how many and their worth, how many are done,
 * View all), one To do list (loose ends, visit checks, money and quotes waiting, each with a count and its worth), and
 * Business health (job value and visits this week and the past 30 days, each with the change from the time before).
 */
import { Link } from "@tanstack/react-router";
import { addDays, mondayOf, type HomeAnswer, type MoneyToDo, type RequestStats, type VisitCard } from "@steward/shared";
import type { ReactNode } from "react";
import { money, plural, shortDate, timeRange } from "../components/format";
import { Icon, type IconName } from "../components/Icon";
import { EmptyState, Skeleton } from "../components/parts";
import { VisitMap } from "../components/VisitMap";

export interface TodoRow {
  key: string;
  what: string;
  hint: string;
  to: string;
  params?: { ref: string };
  icon?: IconName;
}

/** Money and quotes waiting, in the old Steward's words, for the phone's To do. */
export function moneyRows(m: MoneyToDo | undefined, r: RequestStats | null): TodoRow[] {
  const rows: TodoRow[] = [];
  if (r?.counts.new)
    rows.push({
      key: "req",
      icon: "requests",
      what: plural(r.counts.new, "new request"),
      hint: "Check the price and send the quote",
      to: "/todo/new_requests",
    });
  if (!m) return rows;
  const inv = (n: number) => (n === 1 ? "invoice" : "invoices");
  const qw = (n: number) => (n === 1 ? "quote" : "quotes");
  if (m.toCheck)
    rows.push({
      key: "check",
      icon: "invoices",
      what: `${String(m.toCheck)} ${inv(m.toCheck)} to check and send`,
      hint: `Worth ${money(m.toCheckCents)}`,
      to: "/todo/to_check",
    });
  if (m.toCollect)
    rows.push({
      key: "collect",
      icon: "money",
      what: `${String(m.toCollect)} cash or check to collect`,
      hint: "Mark each one when the money is in hand",
      to: "/invoices",
    });
  if (m.pastDue)
    rows.push({
      key: "late",
      icon: "invoices",
      what: `${String(m.pastDue)} ${inv(m.pastDue)} past due`,
      hint: `Worth ${money(m.pastDueCents)}`,
      to: "/todo/past_due",
    });
  if (m.awaiting)
    rows.push({
      key: "wait",
      icon: "invoices",
      what: `${String(m.awaiting)} ${inv(m.awaiting)} waiting for payment`,
      hint: `Worth ${money(m.awaitingCents)}`,
      to: "/todo/awaiting",
    });
  if (m.quotesApproved)
    rows.push({
      key: "qa",
      icon: "quotes",
      what: `${String(m.quotesApproved)} approved ${qw(m.quotesApproved)} to schedule`,
      hint: `Worth ${money(m.quotesApprovedCents)}`,
      to: "/quotes",
    });
  if (m.quotesWaiting)
    rows.push({
      key: "qw",
      icon: "quotes",
      what: `${String(m.quotesWaiting)} ${qw(m.quotesWaiting)} waiting on the client`,
      hint: `Worth ${money(m.quotesWaitingCents)}`,
      to: "/todo/quotes_waiting",
    });
  return rows;
}

const initials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .map((w) => w[0] ?? "")
    .slice(0, 2)
    .join("")
    .toUpperCase();

/** "13:05": this phone's clock now, to tell a visit that should have started. */
function nowHm(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export function PhoneHome({
  title,
  date,
  today,
  banners,
  seesWork,
  todays,
  loading,
  todo,
  health,
  crew = false,
  top,
}: {
  title: string;
  date: string;
  today: string;
  banners: ReactNode;
  seesWork: boolean;
  todays: VisitCard[];
  loading: boolean;
  todo: TodoRow[];
  health: HomeAnswer | undefined;
  /** A crew member's Home (owner, Oct 31): the same look with only their visits, no money, no To do. */
  crew?: boolean;
  /** On top of the map under the greeting, like Jobber (owner, Oct 31: the crew clock). */
  top?: ReactNode;
}) {
  const done = todays.filter((v) => v.status === "done").length;
  const worth = todays.reduce((s, v) => s + v.totalCents, 0);
  const hm = nowHm();
  const sorted = todays.slice().sort((a, b) => (a.startTime ?? "99").localeCompare(b.startTime ?? "99"));
  return (
    <div className="phome">
      {seesWork ? (
        <div className="phome-hero">
          <div className="phome-map">
            <VisitMap visits={todays} bare live />
          </div>
          <header className="phome-head">
            <span>{date}</span>
            <h1>{title}</h1>
          </header>
          {top ? <div className="phome-top">{top}</div> : null}
          <Link to={crew ? "/my-schedule" : "/schedule"} className="phome-mapall">
            View all <Icon name="chevronRight" size={18} />
          </Link>
        </div>
      ) : (
        <header className="phome-head plain">
          <span>{date}</span>
          <h1>{title}</h1>
        </header>
      )}
      {seesWork ? null : top}
      {seesWork ? (
        <section className="phome-today">
          {loading ? (
            <Skeleton rows={2} />
          ) : (
            <div className="phome-visits">
              {sorted.length ? (
                sorted.map((v) => {
                  const late = v.date === today && v.status === "scheduled" && v.startTime !== null && v.startTime < hm;
                  return (
                    <Link
                      key={v.ref}
                      to={crew ? "/my-day/$ref" : "/visits/$ref"}
                      params={{ ref: v.ref }}
                      className={`phome-visit${v.status === "done" ? " done" : ""}`}
                    >
                      <b>{v.clientName}</b>
                      <span className="phome-row">
                        <span className="num">{timeRange(v.startTime, v.endTime).replace(" to ", " - ")}</span>
                        {v.status === "done" ? (
                          <span className="phome-st ok">Done</span>
                        ) : v.status === "in_progress" ? (
                          <span className="phome-st on">In progress</span>
                        ) : late ? (
                          <span className="phome-st late">Late</span>
                        ) : null}
                      </span>
                      {v.address ? <span className="phome-addr">{v.address}</span> : null}
                      <span className="phome-row">
                        <span className="muted">{v.jobName}</span>
                        <span className="phome-ppl">
                          {v.crew.map((c) => (
                            <i key={c.id} title={c.name} style={c.color ? { borderColor: c.color } : undefined}>
                              {initials(c.name)}
                            </i>
                          ))}
                        </span>
                      </span>
                    </Link>
                  );
                })
              ) : (
                <div className="phome-visit none">No visits today</div>
              )}
              {crew ? null : (
                <Link to="/jobs/new" className="phome-visit add">
                  <Icon name="plus" size={22} /> Schedule a job
                </Link>
              )}
            </div>
          )}
          {todays.length ? (
            <p className="phome-sum">
              <b className="num">
                {plural(todays.length, "visit")}
                {crew ? "" : ` worth ${money(worth)}`}
              </b>
              <span className="muted">
                {" "}
                · {done} of {todays.length} done
              </span>
            </p>
          ) : null}
        </section>
      ) : null}
      {/* After the visit cards: they ride up over the map, so anything between them and the map was covered. */}
      {banners}
      {crew ? null : (
        <section>
          <h2 className="phome-title">To do</h2>
          {todo.length ? (
            <div className="phome-list">
              {todo.map((t) => (
                <Link key={t.key} to={t.to} {...(t.params ? { params: t.params } : {})} className="phome-item">
                  <span className={`phome-ico ${t.icon ?? "warning"}`}>
                    <Icon name={t.icon ?? "warning"} size={22} />
                  </span>
                  <span>
                    <b>{t.what}</b>
                    {t.hint ? <span className="muted">{t.hint}</span> : null}
                  </span>
                  <Icon name="arrowRight" size={22} />
                </Link>
              ))}
            </div>
          ) : (
            <EmptyState icon="check" title="Nothing needs you">
              Late visits, invoices to send and quotes waiting show here.
            </EmptyState>
          )}
        </section>
      )}
      {health && !crew ? <Health h={health} /> : null}
    </div>
  );
}

/** "↑ 12%" from the time before, or nothing when there is nothing to compare with. */
function Change({ now, before }: { now: number; before: number }) {
  if (!(before > 0)) return null;
  const p = Math.round(((now - before) / before) * 100);
  if (!p) return null;
  return (
    <span className={`phome-chg ${p > 0 ? "up" : "down"}`}>
      {p > 0 ? "↑" : "↓"} {Math.abs(p)}%
    </span>
  );
}

function Health({ h }: { h: HomeAnswer }) {
  const span = (from: string, to: string) => `${shortDate(from)} - ${shortDate(to)}`;
  const monday = mondayOf(h.today);
  const week = span(monday, addDays(monday, 6));
  const month = span(addDays(h.today, -29), h.today);
  const rows: { what: string; when: string; big: string; now: number; before: number }[] = [
    {
      what: "Job value",
      when: `This week (${week})`,
      big: money(h.weekValueCents),
      now: h.weekValueCents,
      before: h.lastWeekValueCents,
    },
    {
      what: "Visits scheduled",
      when: `This week (${week})`,
      big: String(h.weekVisits),
      now: h.weekVisits,
      before: h.lastWeekVisits,
    },
    {
      what: "Job value",
      when: `Past 30 days (${month})`,
      big: money(h.monthValueCents),
      now: h.monthValueCents,
      before: h.lastMonthValueCents,
    },
    {
      what: "Visits scheduled",
      when: `Past 30 days (${month})`,
      big: String(h.monthVisits),
      now: h.monthVisits,
      before: h.lastMonthVisits,
    },
  ];
  return (
    <section>
      <div className="phome-titlerow">
        <h2 className="phome-title">Business health</h2>
        <Link to="/insights" className="phome-link">
          View all
        </Link>
      </div>
      <div className="phome-list">
        {rows.map((r) => (
          <div key={r.what + r.when} className="phome-item static">
            <span>
              <b>{r.what}</b>
              <span className="muted">{r.when}</span>
            </span>
            <span className="phome-val">
              <b className="num">{r.big}</b>
              <Change now={r.now} before={r.before} />
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}
